/**
 * TopDisciplines - Shows the most popular disciplines by engagement.
 */

interface Discipline {
  name: string;
  percentage: number;
}

interface TopDisciplinesProps {
  disciplines: Discipline[];
  loading?: boolean;
  title?: string;
}

const DISCIPLINE_COLORS: Record<string, string> = {
  Math: "bg-purple-500",
  Spelling: "bg-green-500",
  Patterns: "bg-gradient-to-r from-red-500 via-yellow-500 to-blue-500",
  Typing: "bg-blue-500",
  Programming: "bg-orange-500",
};

export function TopDisciplines({
  disciplines,
  loading = false,
  title = "Top Disciplines",
}: TopDisciplinesProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="h-6 w-32 bg-gray-200 rounded mb-4 animate-pulse" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex justify-between mb-1">
                <div className="h-4 w-20 bg-gray-200 rounded" />
                <div className="h-4 w-10 bg-gray-200 rounded" />
              </div>
              <div className="h-2 bg-gray-100 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {disciplines.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-8">
          No discipline data yet
        </div>
      ) : (
        <div className="space-y-4">
          {disciplines.map((discipline, index) => (
            <div key={discipline.name}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-700">
                  {index + 1}. {discipline.name}
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {discipline.percentage}%
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    DISCIPLINE_COLORS[discipline.name] || "bg-gray-400"
                  }`}
                  style={{ width: `${discipline.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TopDisciplines;
